import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, Conditional, Else, Heading, If, mappedArray, Row } from "@hex-insights/core";
import {
	MarketEntranceFilterForm,
	MarketEntranceFilterFormState,
	MarketEntranceFilterFormUtils,
	MarketEntranceFormat,
	MarketEntranceIndexQuery,
	MarketEntranceOrderField,
	MarketEntranceOrderForm,
	MarketEntranceOrderFormState,
	MarketEntranceOrderFormValues,
	MarketEntrancePaginationForm,
	useMarketEntranceIndexQuery,
} from "@hex-insights/roadium.shared";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { Table } from "@hex-insights/tables";
import { Main } from "../../../../Components";
import { marketEntranceHubCreatePageInfo } from "../CreatePage/pageinfo";
import { marketEntranceHubDetailPageInfo } from "../DetailPage/pageinfo";
import { marketEntranceHubIndexPageInfo } from "./pageinfo";

const columns: (keyof MarketEntranceIndexQuery["marketEntranceConnection"]["edges"][0]["node"])[] = ["name"];

function orderFieldToColumn(field: MarketEntranceOrderField | null) {
	if (field === null) {
		return "id";
	}
	return MarketEntranceOrderFormValues.orderFieldToModelField[field];
}

function columnToOrderField(column: string) {
	if (column === "id") {
		return null;
	}
	return MarketEntranceOrderFormValues.modelFieldToOrderField[column];
}

export function MarketEntranceHubIndexPage() {
	useActivePageRegistration(marketEntranceHubIndexPageInfo);

	const filterFormState = MarketEntranceFilterFormState.useFilterFormStateWithQueryStateSync();
	const orderFormState = MarketEntranceOrderFormState.useOrderFormStateWithQueryStateSync();
	const paginationFormState = IndexForms.usePaginationFormStateWithQueryStateAndLocalStorageSync();

	const filterInputs = IndexForms.useFilterInput(
		filterFormState.formValues,
		MarketEntranceFilterFormUtils.toFilterInputs,
	);
	const orderInput = IndexForms.useOrderInput(orderFormState.formValues);
	const queryVariables = IndexForms.useQueryVariables(
		filterInputs,
		orderInput,
		paginationFormState.formValues,
		paginationFormState.formSetFunctions.cursor,
	);
	const { loading, data, error } = useMarketEntranceIndexQuery({ variables: queryVariables });

	const clearFilters = React.useCallback(() => {
		MarketEntranceFilterFormUtils.clearFilters(filterFormState);
	}, [filterFormState]);

	const numActiveFilters = MarketEntranceFilterFormUtils.countActiveFilters(filterFormState.formValues);
	const [paginationInfo, setPaginationInfo] = IndexForms.usePaginationInfo();

	const tableSortingProps = IndexForms.useTableSortingFromOrderForm(
		orderFormState,
		orderFieldToColumn,
		columnToOrderField,
	);

	const numFillerRows = Math.max(
		(paginationFormState.formValues.resultsPerPage ?? 0) - (data?.marketEntranceConnection.edges.length ?? 0),
		0,
	);

	return (
		<Main>
			<Row justify="space-between" align="center" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Heading level={1} noMargin>
					Market Entrances
				</Heading>

				<InternalLink to={marketEntranceHubCreatePageInfo.to}>New Market Entrance</InternalLink>
			</Row>

			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.25rem"
				withVerticalSpacing
				overflow="wrap"
				style={{ marginBottom: "0.75rem" }}
			>
				<MarketEntranceFilterForm.Search formState={filterFormState} />
				<MarketEntranceFilterForm.FilterController formState={filterFormState} numActiveFilters={numActiveFilters} />
				<If condition={numActiveFilters > 0}>
					<Button variant="link" size="small" onClick={clearFilters}>
						Clear
					</Button>
				</If>
				<MarketEntranceOrderForm.OrderController formState={orderFormState} />
			</Row>

			<Table.Container className="hub__index-page__table__container">
				<Table
					columns={columns}
					minColumnWidth="10rem"
					scrollSyncID={paginationFormState.formValues.cursor}
					{...tableSortingProps}
					className="hub__index-page__table"
				>
					<Table.Header className="hub__index-page__table__header">
						<Table.Row>
							<Table.Heading column="name">Name</Table.Heading>
						</Table.Row>
					</Table.Header>

					<Table.Body className="hub__index-page__table__body">
						<Conditional>
							<If condition={loading}>
								<Table.Row className="hub__index-page__table__row">
									<Table.Cell column="name" colspan={columns.length}>
										Loading...
									</Table.Cell>
								</Table.Row>
								{mappedArray((paginationFormState.formValues.resultsPerPage ?? 1) - 1, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>

							<Else>
								{data?.marketEntranceConnection.edges.map((e) => (
									<Table.Row key={e.node.id} className="hub__index-page__table__row">
										<Table.Cell column="name" oneLine>
											<InternalLink to={marketEntranceHubDetailPageInfo.to(e.node.id)}>
												{MarketEntranceFormat.name(e.node)}
											</InternalLink>
										</Table.Cell>
									</Table.Row>
								))}
								{mappedArray(numFillerRows, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</Else>
						</Conditional>
					</Table.Body>
				</Table>

				<Table.Toolbar>
					<Row justify="space-between" align="center">
						<MarketEntrancePaginationForm.PaginationForm
							formState={paginationFormState}
							filters={filterInputs}
							order={orderInput}
							setPaginationInfo={setPaginationInfo}
						/>

						<span>
							<Conditional>
								<If condition={loading}>Loading...</If>
								<If condition={!!error}>Something went wrong.</If>
								<If condition={data?.marketEntranceConnection.edges.length === 0}>No results found.</If>
								<Else>{IndexForms.getPaginationDescription(paginationInfo)}</Else>
							</Conditional>
						</span>
					</Row>
				</Table.Toolbar>
			</Table.Container>
		</Main>
	);
}
